import type { SimEvent } from "../protocol/events";
import { drainUnits, species, totalUnits } from "./composition";
import { CORROSION_HP_PER_TICK } from "./definitions";
import { contaminateTile } from "./hazards";
import { pollute } from "./pollution";
import {
  isPropertyRecipe,
  outgoingEdges,
  recipesForNode,
  resistsCorrosion,
  resolveHoldRecipeForPacket,
  type EngineEdge,
  type EngineNode,
  type EnginePacket,
  type EngineState,
} from "./state";

/**
 * Packet movement, modelled as a conveyor: a pipe holds at most one packet per
 * waypoint, and each packet advances one waypoint per tick.
 *
 * Determinism: within an edge, packets are advanced from the far end backwards,
 * so a waypoint is always vacated before the packet behind tries to claim it.
 * Across edges, iteration is in stable edge-id order. Nothing here depends on
 * evaluation order. `applyCorrosion` does reach the RNG, through `pollute`'s
 * draw-pile insertion — seeded, so the order stays reproducible.
 *
 * Returns the ids of edges that were blocked pushing into a full buffer, which
 * is what the pressure system (`03-hazards.md` §2.2) charges a
 * pressure counter for.
 */
export interface MovementResult {
  /** Edges whose head packet could not be delivered because downstream was full. */
  blockedEdgeIds: string[];
}

export function processMovement(state: EngineState, events: SimEvent[]): MovementResult {
  const blockedEdgeIds: string[] = [];
  /** Edges that carried a corrosive species this tick — charged once, below. */
  const corrodedEdgeIds = new Set<string>();

  // Fresh budget of one move per packet for this tick.
  for (const node of state.nodes) {
    for (const packet of node.buffer) packet.movedThisTick = false;
  }
  for (const edge of state.edges) {
    for (const transit of edge.transit) transit.packet.movedThisTick = false;
  }

  const edges = [...state.edges].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const edge of edges) {
    if (edge.isBurst) continue;
    advanceEdge(state, edge, corrodedEdgeIds, events, blockedEdgeIds);
  }

  // Only after every pipe has advanced do nodes push new packets out, so a
  // packet cannot traverse a whole pipe in one tick.
  const nodes = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const node of nodes) {
    if (node.isDestroyed) continue;
    pushFromNode(state, node, corrodedEdgeIds, events, blockedEdgeIds);
  }

  applyCorrosion(state, corrodedEdgeIds, events);

  return { blockedEdgeIds };
}

/** Advances packets already in a pipe, far end first. */
function advanceEdge(
  state: EngineState,
  edge: EngineEdge,
  corrodedEdgeIds: Set<string>,
  events: SimEvent[],
  blockedEdgeIds: string[],
): void {
  const lastIndex = edge.path.length - 1;

  // Sort descending so the packet nearest the outlet moves first.
  edge.transit.sort((a, b) => b.pathIndex - a.pathIndex);

  const occupied = new Set(edge.transit.map((t) => t.pathIndex));

  for (const transit of edge.transit) {
    if (transit.packet.movedThisTick) continue;

    if (transit.pathIndex >= lastIndex) {
      // At the outlet: try to deliver into the downstream node.
      const target = state.nodes.find((n) => n.id === edge.toNodeId);
      if (!target || target.isDestroyed) continue;

      if (target.buffer.length >= target.bufferCapacity) {
        // `03-hazards.md` §2.2: pushing into a full buffer pressurises the pipe
        // — charged even when a feeding pump's override lets the packet
        // through below (spec 0024 Resolved Question 2: the hazard stays).
        blockedEdgeIds.push(edge.id);
        // `03-hazards.md` §2.3: a corrosive packet blocked here is still
        // resting in this pipe's transit for the tick, so it still corrodes it.
        noteCorrosiveTraffic(edge, transit.packet.composition, state, corrodedEdgeIds);

        // Source node may have been destroyed mid-transit; that's ordinary
        // behaviour (no override), not an error.
        const fromNode = state.nodes.find((n) => n.id === edge.fromNodeId);
        if (target.buffer.length >= target.bufferCapacity + pumpOverrideOf(fromNode)) continue;
        // Within a pushing pump's override headroom (spec 0024 §4.7): the
        // packet still delivers below, over capacity by at most `pumpOverride`.
      }

      occupied.delete(transit.pathIndex);
      edge.transit = edge.transit.filter((t) => t !== transit);
      transit.packet.residenceTicks = 0;
      transit.packet.holdTicks = 0;
      transit.packet.hasCompletedHold = false;
      transit.packet.movedThisTick = true;
      // Arriving somewhere new makes it feedstock again, so the receiving node
      // will hold it for its own reaction.
      transit.packet.isProduct = false;
      // A fraction's `phaseRole` only steers it out of the converter that just
      // produced it (spec 0003); it has no meaning once delivered elsewhere.
      transit.packet.phaseRole = null;
      // Same reasoning for a separator's fraction (spec 0023).
      transit.packet.separationRole = null;
      target.buffer.push(transit.packet);
      continue;
    }

    const nextIndex = transit.pathIndex + 1;
    if (occupied.has(nextIndex)) {
      // Held up behind the packet ahead of it — resting, not advancing, but
      // still sitting in the pipe for the tick, so it still corrodes it.
      noteCorrosiveTraffic(edge, transit.packet.composition, state, corrodedEdgeIds);
      continue;
    }

    occupied.delete(transit.pathIndex);
    occupied.add(nextIndex);

    const from = edge.path[transit.pathIndex]!;
    const to = edge.path[nextIndex]!;
    transit.pathIndex = nextIndex;
    transit.packet.movedThisTick = true;

    events.push({
      type: "packet.moved",
      packetId: transit.packet.id,
      edgeId: edge.id,
      composition: { ...transit.packet.composition },
      from: { ...from },
      to: { ...to },
    });

    noteCorrosiveTraffic(edge, transit.packet.composition, state, corrodedEdgeIds);
  }
}

/**
 * A `flowMover`'s candidate edge order for this push, decided by its
 * player-owned `valveState` before falling back to round-robin (spec 0024).
 * `null`/`open` round-robin unchanged over `roleFiltered` from `splitCursor`;
 * `closed` offers nothing, which falls straight through to `pushFromNode`'s
 * existing "every outgoing pipe was occupied" pressure-charging block —
 * exactly what a round-robin mover hits when every candidate is full;
 * `route` offers exactly the one named edge (or nothing, if the
 * phase/separation role filter already excluded it), so there is never a
 * fallback to another edge — a full route edge blocks, per Resolved
 * Question 3.
 */
function orderForMover(node: EngineNode, roleFiltered: EngineEdge[]): EngineEdge[] {
  const valve = node.valveState;
  if (valve === null || valve.kind === "open") {
    return roleFiltered.map((_, i) => roleFiltered[(node.splitCursor + i) % roleFiltered.length]!);
  }
  if (valve.kind === "closed") return [];
  return roleFiltered.filter((e) => e.id === valve.edgeId);
}

/** True while a mover's valve leaves it on automatic round-robin — the only setting `splitCursor` advances under (spec 0024 Constraints). */
function isRoundRobinValve(node: EngineNode): boolean {
  return node.valveState === null || node.valveState.kind === "open";
}

/**
 * `pumpOverride` is `0` by construction on every non-`flowMover` node
 * (`createInitialState`, `applyHardware`, `installHardware`), so this guard is
 * defensive/documentary rather than load-bearing — it exists so a future
 * `NodeKind` can't silently inherit override behaviour by accident.
 */
function pumpOverrideOf(node: EngineNode | undefined): number {
  return node?.kind === "flowMover" ? node.pumpOverride : 0;
}

/** Moves one packet out of a node into an outgoing pipe, if there is room. */
function pushFromNode(
  state: EngineState,
  node: EngineNode,
  corrodedEdgeIds: Set<string>,
  events: SimEvent[],
  blockedEdgeIds: string[],
): void {
  if (node.buffer.length === 0) return;
  // Hoppers are terminal — collection happens in the objective step. A
  // wasteBuyer is the same shape of sink for byproducts (spec 0023), and a
  // vent is a third: it collects and destroys, never routes on (spec 0032).
  if (node.kind === "productHopper" || node.kind === "wasteBuyer" || node.kind === "vent") return;
  // §3.2 (spec 0006, Open Question 3): a scripted power surge freezes every
  // flowMover for the tick — it moves no packets, which routes straight into
  // blockedEdgeIds through the existing full-buffer checks below.
  if (node.kind === "flowMover" && state.powerFreezeTicks > 0) return;

  // A packet that already moved this tick (it just arrived) waits for the
  // next one, which is what holds throughput to 1 unit/tick. A raw reagent
  // still waiting for its reaction partner is held too — otherwise a reactor
  // ships each precursor onward as it lands and nothing ever reacts. An
  // incubator packet that hasn't finished its configured dwell is held too.
  const readyIndex = node.buffer.findIndex(
    (p) => !p.movedThisTick && !isAwaitingReaction(state, node, p) && !isHeldByIncubator(state, node, p),
  );
  if (readyIndex === -1) return;

  // `03-hazards.md` §2.5: a sludged node pushes at 0.5 units/tick too, on the
  // same cadence its reaction and phase-conversion passes just ran on —
  // `resolveSludgeGate` (tick.ts, step 2) already resolved and toggled this
  // tick's answer; reading it again here (rather than re-toggling) is what
  // keeps all three in lockstep. Below, this gates a separator's whole
  // multi-role pass exactly as it gates everyone else's single push — an
  // inert tick pushes nothing, whichever branch runs.
  //
  // Spec 0033 §3.3: an infested node's push is throttled on the same
  // alternate-tick cadence, read off `verminActiveThisTick`
  // (`resolveInfestationGate`, tick.ts step 2) rather than a locked valve
  // setting — `setValve` no longer refuses against an infested `flowMover`.
  if (!node.sludgeActiveThisTick || !node.verminActiveThisTick) return;

  // A fed separator gets one push PER `separationRole` this tick — `main`,
  // then `waste`, then untagged `null` — rather than the single push every
  // other node kind gets below. `processSeparation` replaces the node's
  // whole buffer with up to two fractions every tick; a single push shipped
  // only the first of them and left the other re-pooled into the next tick's
  // split forever, growing without ever charging pressure (G-G19, spec
  // 0030 Open Question 1, Option A).
  if (node.kind === "separator") {
    for (const role of ["main", "waste", null] as const) {
      pushSeparatorRole(state, node, role, corrodedEdgeIds, events, blockedEdgeIds);
    }
    return;
  }

  const candidates = outgoingEdges(state, node.id);
  if (candidates.length === 0) return;

  // A converted fraction is only routable down the edge whose `phaseRole`
  // matches it (spec 0003, Routing). This is scoped to packets that actually
  // carry a `phaseRole` — a converter's untouched raw feed still has
  // `phaseRole: null` and takes ordinary edge selection, or a converter with
  // only typed output edges would strand its own feedstock forever.
  const readyPacket = node.buffer[readyIndex]!;
  const roleFiltered =
    node.kind === "phaseConverter" && readyPacket.phaseRole !== null
      ? candidates.filter((e) => e.phaseRole === readyPacket.phaseRole)
      : candidates;

  // Flow movers round-robin across their outputs, unless a player-set valve
  // says otherwise (spec 0024); everything else takes the first available
  // pipe. All three are deterministic.
  const ordered =
    node.kind === "flowMover" ? orderForMover(node, roleFiltered) : roleFiltered;

  for (const edge of ordered) {
    const occupiedFirst = edge.transit.some((t) => t.pathIndex === 0);
    if (occupiedFirst) continue;

    // A single-waypoint pipe delivers straight into the target.
    const target = state.nodes.find((n) => n.id === edge.toNodeId);
    if (!target || target.isDestroyed) continue;

    if (edge.path.length <= 1) {
      if (target.buffer.length >= target.bufferCapacity) {
        // Charged regardless of whether a pump override lets the push
        // through below (spec 0024 Resolved Question 2: the hazard stays).
        blockedEdgeIds.push(edge.id);
        if (target.buffer.length >= target.bufferCapacity + pumpOverrideOf(node)) continue;
        // Within `node`'s own override headroom (spec 0024 §4.7) — falls
        // through and pushes, over capacity by at most `pumpOverride`.
      }
      const packet = node.buffer.splice(readyIndex, 1)[0]!;
      packet.residenceTicks = 0;
      packet.holdTicks = 0;
      packet.hasCompletedHold = false;
      packet.movedThisTick = true;
      packet.isProduct = false;
      packet.phaseRole = null;
      packet.separationRole = null;
      target.buffer.push(packet);
      // A route setting replaces the cursor rather than biasing it (spec
      // 0024 Constraints), so clearing the valve back to open/null resumes
      // round-robin exactly where it left off.
      if (node.kind === "flowMover" && isRoundRobinValve(node)) {
        node.splitCursor = (node.splitCursor + 1) % candidates.length;
      }
      return;
    }

    const packet = node.buffer.splice(readyIndex, 1)[0]!;
    packet.residenceTicks = 0;
    packet.holdTicks = 0;
    packet.hasCompletedHold = false;
    packet.movedThisTick = true;
    edge.transit.push({ packet, pathIndex: 0 });

    events.push({
      type: "packet.moved",
      packetId: packet.id,
      edgeId: edge.id,
      composition: { ...packet.composition },
      from: { ...node.pos },
      to: { ...edge.path[0]! },
    });

    noteCorrosiveTraffic(edge, packet.composition, state, corrodedEdgeIds);

    if (node.kind === "flowMover" && isRoundRobinValve(node)) {
      node.splitCursor = (node.splitCursor + 1) % candidates.length;
    }
    return;
  }

  // Every outgoing pipe was occupied, or (for a phase converter) none of them
  // carried the ready packet's `phaseRole` — either way the node is backed
  // up. Charge the pipes feeding INTO this node, which are the ones actually
  // under pressure.
  for (const edge of state.edges) {
    if (edge.toNodeId === node.id && !edge.isBurst && edge.transit.length > 0) {
      blockedEdgeIds.push(edge.id);
    }
  }
}

/**
 * One push attempt for a single `separationRole`, called three times per
 * separator per tick — `main`, then `waste`, then untagged `null` — from
 * `pushFromNode` (spec 0030, G-G19). Otherwise the same shape as the single
 * push every other node kind gets: find the role's own ready packet, filter
 * candidate edges to its role (or leave them unfiltered for `null`, exactly
 * the ordinary-edge-selection fallback a phase/separation role has always
 * had), deliver down the first free one, or charge the pipes feeding INTO
 * this node if none is free. At most one delivery per role per tick.
 *
 * The ready-packet search is scoped to `role` and re-run fresh on every call
 * — a `main` push that succeeds splices the buffer and shifts every index
 * behind it, so `waste`'s search can't reuse an index found before it.
 */
function pushSeparatorRole(
  state: EngineState,
  node: EngineNode,
  role: "main" | "waste" | null,
  corrodedEdgeIds: Set<string>,
  events: SimEvent[],
  blockedEdgeIds: string[],
): void {
  const readyIndex = node.buffer.findIndex(
    (p) =>
      !p.movedThisTick &&
      p.separationRole === role &&
      !isAwaitingReaction(state, node, p) &&
      !isHeldByIncubator(state, node, p),
  );
  if (readyIndex === -1) return;

  const candidates = outgoingEdges(state, node.id);
  if (candidates.length === 0) return;

  const roleFiltered = role !== null ? candidates.filter((e) => e.separationRole === role) : candidates;

  for (const edge of roleFiltered) {
    const occupiedFirst = edge.transit.some((t) => t.pathIndex === 0);
    if (occupiedFirst) continue;

    const target = state.nodes.find((n) => n.id === edge.toNodeId);
    if (!target || target.isDestroyed) continue;

    if (edge.path.length <= 1) {
      if (target.buffer.length >= target.bufferCapacity) {
        // Charged regardless of whether a pump override lets the push
        // through below (spec 0024 Resolved Question 2: the hazard stays).
        blockedEdgeIds.push(edge.id);
        if (target.buffer.length >= target.bufferCapacity + pumpOverrideOf(node)) continue;
        // Within `node`'s own override headroom (spec 0024 §4.7) — falls
        // through and pushes, over capacity by at most `pumpOverride`.
      }
      const packet = node.buffer.splice(readyIndex, 1)[0]!;
      packet.residenceTicks = 0;
      packet.holdTicks = 0;
      packet.hasCompletedHold = false;
      packet.movedThisTick = true;
      packet.isProduct = false;
      packet.phaseRole = null;
      packet.separationRole = null;
      target.buffer.push(packet);
      return;
    }

    const packet = node.buffer.splice(readyIndex, 1)[0]!;
    packet.residenceTicks = 0;
    packet.holdTicks = 0;
    packet.hasCompletedHold = false;
    packet.movedThisTick = true;
    edge.transit.push({ packet, pathIndex: 0 });

    events.push({
      type: "packet.moved",
      packetId: packet.id,
      edgeId: edge.id,
      composition: { ...packet.composition },
      from: { ...node.pos },
      to: { ...edge.path[0]! },
    });

    noteCorrosiveTraffic(edge, packet.composition, state, corrodedEdgeIds);
    return;
  }

  // This role's fraction found no free matching edge — the same fallback a
  // single-push node hits, reused as-is rather than scoped to this role's
  // own feeding edges: a duplicate push into `blockedEdgeIds` from a second
  // failed role this tick is a no-op, since `accumulatePressure` dedups by
  // edge id before charging (spec 0030 Constraints).
  for (const edge of state.edges) {
    if (edge.toNodeId === node.id && !edge.isBurst && edge.transit.length > 0) {
      blockedEdgeIds.push(edge.id);
    }
  }
}

/**
 * True when this packet is a raw reagent for a recipe the node can run, but
 * the node hasn't gathered the rest of the inputs yet — so it should sit and
 * wait rather than be shipped downstream.
 *
 * If the partner never arrives the packet eventually sludges (§2.5), which is
 * the correct, recoverable failure: a stalled reactor, cleared by Solvent Flush.
 */
function isAwaitingReaction(state: EngineState, node: EngineNode, packet: EnginePacket): boolean {
  // Whatever this node just made is finished here and moves on, even when its
  // species also appear among some recipe's inputs — which is exactly the case
  // for a purification step (NaCl + H2O -> NaCl) or the intermediate in a
  // two-step synthesis. Without this they would wait at their own maker.
  if (packet.isProduct) return false;

  // A `primarySpecies` property recipe pools the whole buffer and needs
  // k >= 1 before it does anything (spec 0023 Open Question 2), exactly like
  // an ordinary reaction — unlike `boilingPoint`/`holdDuration`, which act
  // per-packet with no pooling minimum and so are still excluded here. Without
  // this, a raw packet sitting in a separator whose pool hasn't yet reached
  // k=1 would be judged "not awaiting reaction" and pushed out unseparated,
  // its `separationRole` still null, so the ordinary edge-selection above
  // would let it through unrestricted and defeat pooling entirely.
  const recipes = recipesForNode(state, node).filter(
    (r) => !isPropertyRecipe(r) || r.primarySpecies !== undefined,
  );
  if (recipes.length === 0) return false;

  const packetSpecies = species(packet.composition);
  if (packetSpecies.length === 0) return false;

  // Held whenever the packet is nothing BUT reagent for a recipe this node can
  // run. Checking "is the recipe satisfiable right now?" instead would ship the
  // reagent out on the very tick its partner lands, because reactions resolve
  // in step 2 and that delivery happens in step 3 — the partner arrives too
  // late to save it. Products contain no input species, so they pass straight
  // through.
  return recipes.some((recipe) => {
    const inputSpecies = species(recipe.inputs);
    return packetSpecies.every((s) => inputSpecies.includes(s));
  });
}

/**
 * True while a packet sitting in an `incubator` hasn't yet dwelt for its
 * configured hold — the node's whole reason to exist (§4: "Holds packets for
 * N consecutive ticks before releasing downstream").
 *
 * Resolved per packet against its own composition (`resolveHoldRecipeForPacket`,
 * spec 0025), not pooled against the node's whole buffer — two incubators of
 * the same kind can run different holds without one packet's presence
 * deciding another's fate. A packet with no matching hold recipe is never
 * held. One with a `minTemperature` gate it doesn't meet is never held
 * either, and leaves untransformed on its next step 3 — its own
 * `EnginePacket.holdTicks` never accrues (`accumulateResidence`, hazards.ts),
 * so it can sit indefinitely below threshold without ever completing the
 * hold. `packet.holdTicks` is what counts against the recipe's
 * `holdDuration`, distinct from `residenceTicks` (`03-hazards.md` §2.5's
 * sludge clock, which keeps counting regardless — open question 6 accepts
 * that a long enough hold sludges its own packet as the designed cost of
 * incubation). A packet that already completed its hold this visit
 * (`hasCompletedHold`, spec 0025) is never held again, regardless of what its
 * possibly-new composition now matches — one hold per visit.
 */
function isHeldByIncubator(state: EngineState, node: EngineNode, packet: EnginePacket): boolean {
  if (node.kind !== "incubator") return false;
  if (packet.hasCompletedHold) return false;
  const recipe = resolveHoldRecipeForPacket(state, node, packet);
  if (recipe === undefined) return false;
  if (recipe.minTemperature !== undefined && packet.temperature < recipe.minTemperature) return false;
  return packet.holdTicks < recipe.holdDuration!;
}

/** Records that a corrosive packet was present in this pipe's transit this tick. */
function noteCorrosiveTraffic(
  edge: EngineEdge,
  composition: Parameters<typeof species>[0],
  state: EngineState,
  corrodedEdgeIds: Set<string>,
): void {
  if (resistsCorrosion(edge) || edge.hp <= 0) return;
  if (species(composition).some((s) => state.stage.corrosiveSpecies.includes(s))) {
    corrodedEdgeIds.add(edge.id);
  }
}

/**
 * `03-hazards.md` §2.3: corrosive reagents cost a standard pipe HP EVERY TICK —
 * not per packet. Charging per packet-move would let a busy pipe lose 30 HP in
 * a tick and leak three times faster than the rules say.
 */
function applyCorrosion(state: EngineState, corrodedEdgeIds: Set<string>, events: SimEvent[]): void {
  for (const edgeId of [...corrodedEdgeIds].sort()) {
    const edge = state.edges.find((e) => e.id === edgeId);
    if (!edge || edge.hp <= 0) continue;

    edge.hp = Math.max(0, edge.hp - CORROSION_HP_PER_TICK);
    events.push({ type: "pipe.corroded", edgeId: edge.id, hp: edge.hp });

    if (edge.hp === 0 && !edge.isLeaking) {
      edge.isLeaking = true;
      events.push({ type: "pipe.leaking", edgeId: edge.id });
      // Running corrosives through unprotected pipe until it opens up is the
      // textbook neglect of `02-cards-and-economy.md` §2.1; it costs a hazard card.
      pollute(state, "leak", edge.id, events);
    }
  }
}

/**
 * A leaking pipe loses exactly 1 integer unit per tick total, from the head
 * packet (the transit packet with the highest pathIndex, closest to the
 * outlet / delivery). Per docs/design/03-hazards.md §2.3 and spec 0004,
 * the loss is apportioned from the largest-magnitude species in that packet,
 * ties broken by sorted order. Everything lost is tallied as waste, because
 * product on the floor is exactly what the score should punish.
 */
export function applyLeakLosses(state: EngineState, events: SimEvent[]): void {
  const edges = [...state.edges].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const edge of edges) {
    if (!edge.isLeaking || edge.isBurst) continue;

    // Find the head packet: highest pathIndex (closest to outlet).
    let headTransit = null;
    let maxPathIndex = -1;
    for (const transit of edge.transit) {
      if (transit.pathIndex > maxPathIndex) {
        maxPathIndex = transit.pathIndex;
        headTransit = transit;
      }
    }

    // If there is a head packet, drain 1 unit from it.
    if (headTransit) {
      const before = totalUnits(headTransit.packet.composition);
      headTransit.packet.composition = drainUnits(headTransit.packet.composition, 1);
      const after = totalUnits(headTransit.packet.composition);
      state.wastedUnits += before - after;
      // §2.3 / open question 2: the loss lands on the grid tile at the
      // waypoint it was just charged at — with 0004 landed, the head packet's.
      contaminateTile(state, edge.path[headTransit.pathIndex]!, "leak", edge.id, events);
    }

    // A packet drained to nothing simply disappears down the drain.
    edge.transit = edge.transit.filter((t) => totalUnits(t.packet.composition) > 0);
  }
}
